/**
 * `createAttachmentUploadRoute` — the fleet-primitive durable-store upload
 * route: a two-phase batch (every file is validated before any file is written),
 * with a stable legacy lane and an ownership-safe receipt lane for new callers,
 * a content-sniffed type gate
 * (`checkAttachmentType` over `sniffBinary`'s magic-byte read, not the
 * extension or the browser-reported MIME), binary/text caps with optional
 * per-sniffed-mime overrides, an aggregate byte cap, and sanitized filenames.
 * Storage is fully seamed through the injected
 * `WriteAttachmentFn` or `AtomicAttachmentWriter` (`./attachment-store`) — no
 * default store, the product owns where bytes actually live (vault, object
 * store, …), and auth/rate limiting is entirely the injected `authorize`
 * seam's job: this factory
 * never invents a 401 or 429 response, it only returns `auth.response`
 * verbatim on failure.
 *
 * Lifted from gtm-agent's `src/routes/api.vault.upload.ts` (the hardening
 * lineage other lifted modules in this vertical cite: gtm#584 binary
 * corruption, gtm#592 sniff gate/caps, gtm#593 batch-atomic writes) and
 * generalized the way `resolve-attachments.ts` generalized gtm's read path —
 * the vault-specific pieces (KV vault paths, frontmatter, per-user rate
 * limiting) are all injected seams here, while the validate-then-write phase
 * split and the type/size gate ordering survive byte-for-byte.
 *
 * @remarks Sibling to, NOT an extension of, `./upload.ts`'s
 * `createUploadRoute` — a different persistence model (durable product store
 * vs. inline-`data:`-or-ephemeral-sandbox-workspace). See that module's doc
 * comment for the up-to-date framing between the two.
 */

import type { ChatAttachmentInput, ChatAttachmentKind } from './wire'
import { attachmentKindForMime } from '../chat-store/parts'
import { redactErrorMessage } from '../redact'
import {
  ATTACHMENT_ROLLBACK_FAILURE_CODE,
  ATTACHMENT_ROLLBACK_FAILURE_MESSAGE,
  ATTACHMENT_STORAGE_FAILURE_MESSAGE,
  type AtomicAttachmentWriter,
  immutableAttachmentPath,
  type AttachmentWriteOwnership,
  type AttachmentWriteReceipt,
  type WriteAttachmentFn,
} from './attachment-store'
import {
  inspectAtomicAttachmentWriteResult,
  inspectLegacyAttachmentWriteResult,
} from './attachment-write-safety'
import {
  ALLOWED_ATTACHMENT_SNIFFED_MIMES,
  ATTACHMENT_MAX_COUNT,
  MAX_ATTACHMENT_TOTAL_BYTES,
  MAX_BINARY_ATTACHMENT_BYTES,
  MAX_TEXT_ATTACHMENT_BYTES,
  attachmentSizeErrorMessage,
  attachmentTotalSizeErrorMessage,
  checkAttachmentType,
  sanitizeAttachmentFileName,
} from './attachment-validation'
import { sniffBinary } from './binary-sniff'
import { defaultValidateAttachmentPath, type AttachmentPathCheck } from './resolve-attachments'
import { sniffMimeFromName } from './promote-file-part'

/** Stable authorization result for the original writer contract. */
export type AttachmentUploadAuthorization =
  | {
      ok: true
      scopeId: string
      writeAttachment?: WriteAttachmentFn
    }
  | { ok: false; response: Response }

/** Authorization result for an ownership-safe attachment writer. */
export type AtomicAttachmentUploadAuthorization =
  | {
      ok: true
      scopeId: string
      attachmentWriter?: AtomicAttachmentWriter
    }
  | { ok: false; response: Response }

/** The logger receives only sanitized backend details. The client receives the
 * opaque message below, regardless of the store's failure text. */
export type AttachmentUploadLogger = Pick<Console, 'error'>

function logAttachmentUploadError(
  logger: AttachmentUploadLogger,
  message: string,
  fields: Record<string, unknown>,
): void {
  try {
    const safeFields = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [
        key,
        typeof value === 'string' ? redactErrorMessage(value) : value,
      ]),
    )
    logger.error(message, safeFields)
  } catch {
    // Logging must not turn a typed storage failure into a thrown response.
  }
}

interface AttachmentUploadRouteCommonOptions {
  /** Overridable caps. Defaults come from `./attachment-validation`. */
  limits?: {
    /** Most files one request may carry. Default {@link ATTACHMENT_MAX_COUNT}. */
    maxCount?: number
    /** Ceiling on a binary file's raw size. Default {@link MAX_BINARY_ATTACHMENT_BYTES}. */
    maxBinaryBytes?: number
    /** Optional binary-file ceilings keyed by the content-sniffed mime. */
    maxBytesBySniffedMime?: ReadonlyMap<string, number>
    /** Ceiling on a text file's raw size. Default {@link MAX_TEXT_ATTACHMENT_BYTES}. */
    maxTextBytes?: number
    /** Aggregate raw-byte ceiling. Default {@link MAX_ATTACHMENT_TOTAL_BYTES}. */
    maxTotalBytes?: number
  }
  /** Attachment kinds this route accepts. Default `['image', 'file']`. */
  allowedKinds?: ChatAttachmentKind[]
  /** Sniffed-mime allowlist fed to `checkAttachmentType`. */
  allowedSniffedMimes?: ReadonlySet<string>
  /** Sanitized-name → logical store path. */
  pathFor?: (name: string) => string
  /** Store-path validator. Default {@link defaultValidateAttachmentPath}. */
  validatePath?: (path: string) => AttachmentPathCheck
  /** Last-resort media-type hook for text content the sniffer cannot type. */
  sniffMime?: (name: string) => string
  /** Unique id source for atomic ownership keys. */
  createWriteId?: () => string
  /** Server-side error sink. Backend text is sanitized before it is logged. */
  logger?: AttachmentUploadLogger
}

/** Stable route options for products using the original writer contract. */
export interface CreateLegacyAttachmentUploadRouteOptions extends AttachmentUploadRouteCommonOptions {
  /** Authenticate the caller, rate-limit, and resolve the store scope
   *  (workspace/tenant id) — never a query param. */
  authorize(args: { request: Request }): Promise<AttachmentUploadAuthorization>
  /** Stable writer. `authorize` may override it per-request. */
  writeAttachment: WriteAttachmentFn
}

/**
 * The original public route-options interface remains available for consumers
 * that extend it. Ownership-safe callers use the atomic interface below.
 */
export interface CreateAttachmentUploadRouteOptions extends CreateLegacyAttachmentUploadRouteOptions {}

/** Ownership-safe route options for new products. */
export interface CreateAtomicAttachmentUploadRouteOptions extends AttachmentUploadRouteCommonOptions {
  /** Authenticate the caller, rate-limit, and resolve the store scope. */
  authorize(args: { request: Request }): Promise<AtomicAttachmentUploadAuthorization>
  /** Complete writer + ambiguous-write cleanup adapter. */
  attachmentWriter: AtomicAttachmentWriter
}

function attachmentUploadError(status: number, code: string, message: string, path?: string): Response {
  return Response.json(
    { error: path === undefined ? { code, message } : { code, message, path } },
    { status },
  )
}

function atomicAttachmentFailure(path: string, cleanupFailures = 0): Response {
  return attachmentUploadError(
    503,
    cleanupFailures > 0 ? ATTACHMENT_ROLLBACK_FAILURE_CODE : 'attachment_store_unavailable',
    cleanupFailures > 0 ? ATTACHMENT_ROLLBACK_FAILURE_MESSAGE : ATTACHMENT_STORAGE_FAILURE_MESSAGE,
    path,
  )
}

interface SuccessfulAttachmentWrite {
  path: string
  ownership: AttachmentWriteOwnership
  receipt: AttachmentWriteReceipt
}

/** Run every available compensation, from the newest write to the oldest. The
 * receipt owns the compare-and-delete operation; this route never deletes a
 * logical path itself. */
async function rollbackSuccessfulWrites(
  writes: readonly SuccessfulAttachmentWrite[],
  scopeId: string,
  writer: AtomicAttachmentWriter,
  logger: AttachmentUploadLogger,
): Promise<number> {
  let failures = 0
  for (let index = writes.length - 1; index >= 0; index -= 1) {
    const write = writes[index]!
    try {
      await write.receipt.rollback()
    } catch (error) {
      failures += 1
      logAttachmentUploadError(logger, '[attachment-upload] cleanup failed', {
        path: write.path,
        ownershipId: write.ownership.id,
        error: redactErrorMessage(error),
      })
      failures += await abortOwnership(writer, scopeId, write.ownership, logger)
    }
  }
  return failures
}

async function abortOwnership(
  writer: AtomicAttachmentWriter,
  scopeId: string,
  ownership: AttachmentWriteOwnership,
  logger: AttachmentUploadLogger,
): Promise<number> {
  try {
    await writer.abort(scopeId, ownership)
    return 0
  } catch (error) {
    logAttachmentUploadError(logger, '[attachment-upload] ambiguous write cleanup failed', {
      path: ownership.path,
      ownershipId: ownership.id,
      error: redactErrorMessage(error),
    })
    return 1
  }
}

function isAtomicRouteOptions(
  options: CreateAttachmentUploadRouteOptions | CreateAtomicAttachmentUploadRouteOptions,
): options is CreateAtomicAttachmentUploadRouteOptions {
  return 'attachmentWriter' in options
}

/** Resolve an attachment upload route handler with customizable limits and validation options. */
export function createAttachmentUploadRoute(
  options: CreateAttachmentUploadRouteOptions | CreateAtomicAttachmentUploadRouteOptions,
): (request: Request) => Promise<Response> {
  const atomic = isAtomicRouteOptions(options)
  const maxCount = options.limits?.maxCount ?? ATTACHMENT_MAX_COUNT
  const maxBinaryBytes = options.limits?.maxBinaryBytes ?? MAX_BINARY_ATTACHMENT_BYTES
  const maxBytesBySniffedMime = options.limits?.maxBytesBySniffedMime
  const maxTextBytes = options.limits?.maxTextBytes ?? MAX_TEXT_ATTACHMENT_BYTES
  const maxTotalBytes = options.limits?.maxTotalBytes ?? MAX_ATTACHMENT_TOTAL_BYTES
  const allowedKinds: ChatAttachmentKind[] = options.allowedKinds ?? ['image', 'file']
  const allowedSniffedMimes = options.allowedSniffedMimes ?? ALLOWED_ATTACHMENT_SNIFFED_MIMES
  const pathFor = options.pathFor ?? ((name: string) => name)
  const validatePath = options.validatePath ?? defaultValidateAttachmentPath
  const sniffMime = options.sniffMime ?? sniffMimeFromName
  const createWriteId = atomic
    ? options.createWriteId ?? (() => {
        if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
          throw new Error('attachment upload requires crypto.randomUUID')
        }
        return crypto.randomUUID()
      })
    : undefined
  const logger = options.logger ?? console

  return async function attachmentUpload(request: Request): Promise<Response> {
    const auth = await options.authorize({ request })
    if (!auth.ok) return auth.response
    const atomicWriter = atomic
      ? (auth as Extract<AtomicAttachmentUploadAuthorization, { ok: true }>).attachmentWriter ?? options.attachmentWriter
      : undefined
    const legacyWriter = atomic
      ? undefined
      : (auth as Extract<AttachmentUploadAuthorization, { ok: true }>).writeAttachment ?? options.writeAttachment
    if (atomic && !atomicWriter) {
      return attachmentUploadError(503, 'attachment_store_unavailable', ATTACHMENT_STORAGE_FAILURE_MESSAGE)
    }

    let form: FormData
    try {
      form = await request.formData()
    } catch {
      return attachmentUploadError(400, 'invalid_upload', 'Expected a multipart/form-data body with file fields')
    }
    // Collect every File value regardless of field name — the client may
    // send one field per file or a single repeated field.
    const files: File[] = []
    form.forEach((value) => {
      if (value instanceof File) files.push(value)
    })
    if (files.length === 0) {
      return attachmentUploadError(400, 'invalid_upload', 'No files in the upload body')
    }

    if (files.length > maxCount) {
      return attachmentUploadError(
        400,
        'attachment_count_exceeded',
        `Too many files — the ${maxCount}-file limit was exceeded`,
      )
    }

    // Advisory only: client-controlled `file.size`, so a dishonest caller can
    // slip past it — but an honest oversized batch should fail before a
    // single byte is read. The authoritative aggregate check (against the
    // sniffed/decoded byte length) runs per-file in phase 1, below.
    const advisoryTotal = files.reduce((sum, file) => sum + file.size, 0)
    if (advisoryTotal > maxTotalBytes) {
      return attachmentUploadError(
        413,
        'attachments_total_too_large',
        attachmentTotalSizeErrorMessage(advisoryTotal, maxTotalBytes),
      )
    }

    interface PreparedWrite {
      path: string
      ownershipId?: string
      name: string
      bytes: Uint8Array
      originalName: string
      size: number
      mediaType: string
      kind: ChatAttachmentKind
    }

    // Phase 1: validate every file and prepare its write input WITHOUT
    // writing anything. A batch fails atomically — any file's validation
    // error must reject the whole request before an earlier file in the
    // same batch is persisted.
    const prepared: PreparedWrite[] = []
    const seenLogicalPaths = new Set<string>()
    let totalBytes = 0

    for (const file of files) {
      const bytes = new Uint8Array(await file.arrayBuffer())
      const sniff = sniffBinary(bytes)
      // Attachment paths double as store keys, so the stored name must fit
      // the store charset; the as-uploaded name survives as `originalName`.
      const name = sanitizeAttachmentFileName(file.name)

      const typeCheck = checkAttachmentType(name, sniff, allowedSniffedMimes)
      if (!typeCheck.succeeded) {
        return attachmentUploadError(
          typeCheck.code === 'attachment_type_mismatch' ? 400 : 415,
          typeCheck.code,
          typeCheck.message,
        )
      }

      const kind = attachmentKindForMime(sniff.mime ?? '')
      if (!allowedKinds.includes(kind)) {
        return attachmentUploadError(
          415,
          'attachment_kind_not_allowed',
          `${name} is a "${kind}" attachment, which this upload route does not accept`,
        )
      }

      let limit = maxTextBytes
      if (sniff.binary) {
        limit = sniff.mime === null
          ? maxBinaryBytes
          : maxBytesBySniffedMime?.get(sniff.mime) ?? maxBinaryBytes
      }
      if (bytes.length > limit) {
        return attachmentUploadError(
          413,
          'attachment_too_large',
          attachmentSizeErrorMessage(name, bytes.length, limit),
        )
      }

      const logicalPath = pathFor(name)
      let ownershipId: string | undefined
      let path = logicalPath
      if (atomic) {
        try {
          ownershipId = createWriteId!()
          path = immutableAttachmentPath(logicalPath, ownershipId)
        } catch (error) {
          logAttachmentUploadError(logger, '[attachment-upload] could not allocate ownership key', {
            path: logicalPath,
            error: redactErrorMessage(error),
          })
          return attachmentUploadError(
            503,
            'attachment_store_unavailable',
            ATTACHMENT_STORAGE_FAILURE_MESSAGE,
            logicalPath,
          )
        }
      }
      const pathCheck = validatePath(path)
      if (!pathCheck.succeeded) {
        return attachmentUploadError(400, 'invalid_attachment_path', pathCheck.error, path)
      }
      // Small hardening over gtm: a batch whose sanitized names collide onto
      // the same store path (e.g. two variously-cased "Report.PDF" uploads)
      // would otherwise silently overwrite one with the other in phase 2.
      const duplicatePath = atomic ? logicalPath : path
      if (seenLogicalPaths.has(duplicatePath)) {
        return attachmentUploadError(
          400,
          'attachment_duplicate_path',
          `attachments must not repeat a path within one upload: ${duplicatePath}`,
          path,
        )
      }
      seenLogicalPaths.add(duplicatePath)

      // Authoritative aggregate check, against the actual decoded byte
      // length rather than the client-reported `file.size` checked above.
      totalBytes += bytes.length
      if (totalBytes > maxTotalBytes) {
        return attachmentUploadError(
          413,
          'attachments_total_too_large',
          attachmentTotalSizeErrorMessage(totalBytes, maxTotalBytes),
        )
      }

      const mediaType = sniff.mime ?? sniffMime(name)
      prepared.push({ path, ownershipId, name, bytes, originalName: file.name, size: bytes.length, mediaType, kind })
    }

    // Phase 2: every file in the batch passed validation — write them all.
    const uploaded: ChatAttachmentInput[] = []
    const successfulWrites: SuccessfulAttachmentWrite[] = []
    for (const input of prepared) {
      if (!atomic) {
        let written: unknown
        try {
          written = await legacyWriter!(auth.scopeId, input.path, input.bytes, {
            mediaType: input.mediaType,
            name: input.name,
            originalName: input.originalName,
            size: input.size,
          })
        } catch (error) {
          logAttachmentUploadError(logger, '[attachment-upload] legacy writer failed', {
            path: input.path,
            error: redactErrorMessage(error),
          })
          return attachmentUploadError(
            503,
            'attachment_store_unavailable',
            ATTACHMENT_STORAGE_FAILURE_MESSAGE,
            input.path,
          )
        }
        const result = inspectLegacyAttachmentWriteResult(written)
        if (!result) {
          logAttachmentUploadError(logger, '[attachment-upload] legacy writer returned an invalid result', {
            path: input.path,
          })
          return attachmentUploadError(
            503,
            'attachment_store_unavailable',
            ATTACHMENT_STORAGE_FAILURE_MESSAGE,
            input.path,
          )
        }
        if (!result.ok) {
          const reason = redactErrorMessage(result.reason)
          logAttachmentUploadError(logger, '[attachment-upload] legacy write rejected', {
            path: input.path,
            error: reason,
          })
          return attachmentUploadError(413, 'attachment_write_failed', reason, input.path)
        }
        uploaded.push({
          path: input.path,
          name: input.name,
          size: input.size,
          mediaType: input.mediaType,
          kind: input.kind,
        })
        continue
      }

      const ownershipId = input.ownershipId
      if (!ownershipId) {
        return attachmentUploadError(503, 'attachment_store_unavailable', ATTACHMENT_STORAGE_FAILURE_MESSAGE, input.path)
      }
      const ownership: AttachmentWriteOwnership = Object.freeze({ id: ownershipId, path: input.path })
      let written: Awaited<ReturnType<AtomicAttachmentWriter['write']>>
      try {
        written = await atomicWriter!.write(auth.scopeId, input.path, input.bytes, {
          mediaType: input.mediaType,
          name: input.name,
          originalName: input.originalName,
          size: input.size,
          ownership,
        })
      } catch (error) {
        const cleanupFailures = (await abortOwnership(atomicWriter!, auth.scopeId, ownership, logger)) +
          await rollbackSuccessfulWrites(successfulWrites, auth.scopeId, atomicWriter!, logger)
        logAttachmentUploadError(logger, '[attachment-upload] write failed', {
          path: input.path,
          ownershipId: ownership.id,
          cleanupFailures,
          error: redactErrorMessage(error),
        })
        return atomicAttachmentFailure(input.path, cleanupFailures)
      }
      const result = inspectAtomicAttachmentWriteResult(written, ownership)
      if (!result) {
        const cleanupFailures = (await abortOwnership(atomicWriter!, auth.scopeId, ownership, logger)) +
          await rollbackSuccessfulWrites(successfulWrites, auth.scopeId, atomicWriter!, logger)
        logAttachmentUploadError(logger, '[attachment-upload] writer returned a mismatched ownership receipt', {
          path: input.path,
          ownershipId: ownership.id,
          cleanupFailures,
        })
        return atomicAttachmentFailure(input.path, cleanupFailures)
      }
      if (!result.ok) {
        const cleanupFailures = (await rollbackSuccessfulWrites([
          { path: input.path, ownership, receipt: result.receipt },
        ], auth.scopeId, atomicWriter!, logger)) + await rollbackSuccessfulWrites(
          successfulWrites,
          auth.scopeId,
          atomicWriter!,
          logger,
        )
        logAttachmentUploadError(logger, '[attachment-upload] write rejected', {
          path: input.path,
          ownershipId: ownership.id,
          cleanupFailures,
          error: redactErrorMessage(result.reason),
        })
        return atomicAttachmentFailure(input.path, cleanupFailures)
      }
      successfulWrites.push({ path: input.path, ownership, receipt: result.receipt })
      uploaded.push({
        path: input.path,
        name: input.name,
        size: input.size,
        mediaType: input.mediaType,
        kind: input.kind,
      })
    }

    return Response.json({ files: uploaded })
  }
}
